// src/context/customerContext.tsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { Customer } from '../utils/types';
import { fetchAllCustomer } from '../utils/api'; // Make sure this API function exists and works

interface CustomerContextType {
    customers: Customer[];
    selectedSessionCustomer: Customer | null; // State holds the full Customer object including the 'role'
    setSelectedSessionCustomer: (customer: Customer | null) => void;
    loading: boolean;
    error: string | null;
    refreshCustomers: () => void;
}

// Create the context with an undefined initial value
const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// Define the provider component
export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedSessionCustomer, setSelectedSessionCustomer] = useState<Customer | null>(null); // Holds the selected customer object
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Function to fetch all customers from the API
    const loadCustomers = async () => {
        setLoading(true);
        setError(null);
        try {
            // Call your API function to get the list of customers
            // Ensure fetchAllCustomer returns data conforming to the Customer[] type
            const fetchedCustomers = await fetchAllCustomer();
            setCustomers(fetchedCustomers);
        } catch (err: any) {
            console.error("Failed to load customers:", err);
            setError(err.message || 'Failed to fetch customers.');
            setCustomers([]); // Clear customers on error to avoid displaying stale data
        } finally {
            setLoading(false);
        }
    };

    // Fetch customers when the provider mounts for the first time
    useEffect(() => {
        loadCustomers();
    }, []); // Empty dependency array ensures this runs only once on mount

    // Function provided to consumers to manually trigger a refresh
    const refreshCustomers = () => {
        loadCustomers(); // Simply re-run the fetching logic
    };

    // Provide the state and functions to child components
    return (
        <CustomerContext.Provider value={{
            customers,
            selectedSessionCustomer,
            setSelectedSessionCustomer,
            loading,
            error,
            refreshCustomers // Make the refresh function available via context
        }}>
            {children}
        </CustomerContext.Provider>
    );
};

// Custom hook for easy consumption of the context
export const useCustomerContext = () => {
    const context = useContext(CustomerContext);
    // Ensure the hook is used within a CustomerProvider
    if (context === undefined) {
        throw new Error('useCustomerContext must be used within a CustomerProvider');
    }
    return context;
};
